import { Link } from "react-router-dom";
import { ArrowRight, Bell, Lock } from "lucide-react";
import useBrandAsset from "../../../lib/useBrandAsset";

/**
 * Asymmetric editorial department grid. Clothing dominates as the live
 * hero card; Jewellery and Bags are muted "coming soon" cards with a
 * notify CTA. This mirrors the boutique-magazine layout from the brief.
 */
export default function EditorialDepartments() {
    return (
        <section
            data-testid="shop-departments"
            className="mb-20 lg:mb-28"
        >
            <SectionHeader
                overline="The Edit"
                title={<>Curated for every <span className="italic text-wine">celebration</span>.</>}
                subtitle="Three worlds, one boutique. Start with our flagship clothing line — the rest are arriving soon."
            />

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4 lg:gap-6">
                {/* === Dominant: Clothing === */}
                <DominantCard />

                {/* === Coming Soon: stacked === */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 lg:gap-6">
                    <ComingSoonCard
                        title="Jewellery"
                        copy="Kundan, pearls and oxidised-finish pieces to complete your ethnic looks."
                        asset="dept_jewellery"
                        slug="jewellery"
                        testid="dept-jewellery"
                    />
                    <ComingSoonCard
                        title="Bags & Purses"
                        copy="Hand-embroidered potlis and clutches for festive evenings."
                        asset="dept_bags"
                        slug="bags"
                        testid="dept-bags"
                    />
                </div>
            </div>
        </section>
    );
}

function DominantCard() {
    const image = useBrandAsset("dept_clothing");
    return (
        <Link
            to="/d/clothing"
            data-testid="dept-clothing"
            className="group relative block overflow-hidden rounded-3xl bg-ink min-h-[420px] sm:min-h-[480px] lg:min-h-[560px]"
        >
            <img
                src={image}
                alt="Curated Indian clothing — JnS Creation"
                className="absolute inset-0 w-full h-full object-cover object-[50%_30%] transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                loading="lazy"
            />
            {/* Dark editorial overlay bottom-up */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(to top, rgba(28,24,23,0.92) 0%, rgba(28,24,23,0.75) 25%, rgba(28,24,23,0.40) 50%, rgba(28,24,23,0.12) 72%, rgba(28,24,23,0) 88%)",
                }}
            />
            {/* Gold-radial accent */}
            <div
                className="absolute -top-10 -right-10 w-64 h-64 opacity-30 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle, rgba(201,164,122,0.7), transparent 70%)",
                }}
            />

            <div className="relative h-full flex flex-col justify-end p-7 sm:p-10 lg:p-12 text-ivory">
                <p className="text-[10px] uppercase tracking-[0.32em] text-rosegold font-bold mb-3">
                    Clothing · Live now
                </p>
                <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mb-3">
                    Curated Indian wear for every <span className="italic">celebration</span>.
                </h3>
                <p className="text-sm text-ivory/85 max-w-md mb-6 leading-relaxed">
                    Anarkalis, cord sets, kurtis, sarees and more — every piece personally selected by Sunali.
                </p>
                <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-rosegold group-hover:gap-3.5 transition-all">
                    Explore Clothing
                    <ArrowRight size={13} />
                </span>
            </div>
        </Link>
    );
}

function ComingSoonCard({ title, copy, asset, slug, testid }) {
    const image = useBrandAsset(asset);
    return (
        <Link
            to={`/d/${slug}`}
            data-testid={testid}
            className="group relative block overflow-hidden rounded-3xl bg-ivory-soft min-h-[200px] sm:min-h-[230px] lg:min-h-[268px] ring-1 ring-rosegold/15"
        >
            <img
                src={image}
                alt={title}
                className="absolute inset-0 w-full h-full object-cover transition-all duration-[1200ms] ease-out group-hover:scale-[1.06]"
                style={{ filter: "grayscale(0.6) brightness(0.85)" }}
                loading="lazy"
            />
            {/* Soft ivory wash so it reads as muted */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(to top, rgba(28,24,23,0.92) 0%, rgba(28,24,23,0.72) 25%, rgba(28,24,23,0.45) 50%, rgba(28,24,23,0.20) 75%, rgba(28,24,23,0.10) 100%)",
                }}
            />
            <div className="relative h-full flex flex-col justify-end p-6 lg:p-7">
                <div className="inline-flex self-start items-center gap-1.5 px-2.5 py-1 rounded-full
                                bg-ivory/95 backdrop-blur text-[9px] uppercase tracking-[0.22em] font-bold text-wine mb-2">
                    <Lock size={9} /> Coming Soon
                </div>
                <h4 className="font-display text-2xl text-ivory leading-tight mb-1">{title}</h4>
                <p className="text-[12px] text-ivory/85 max-w-xs leading-relaxed mb-3">{copy}</p>
                <span
                    data-testid={`${testid}-notify`}
                    className="inline-flex self-start items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-rosegold group-hover:text-ivory transition-colors"
                >
                    <Bell size={11} /> Notify me when live
                </span>
            </div>
        </Link>
    );
}

export function SectionHeader({ overline, title, subtitle, align = "left" }) {
    const alignCls = align === "center" ? "text-center mx-auto" : "";
    return (
        <header className={`max-w-3xl ${alignCls}`}>
            <p className="text-[10px] uppercase tracking-[0.32em] text-rosegold font-bold mb-3 flex items-center gap-3"
               style={align === "center" ? { justifyContent: "center" } : undefined}>
                <span className="inline-block w-8 h-px bg-rosegold flex-shrink-0" />
                {overline}
            </p>
            <h2 className="font-display text-ink leading-[1.05] tracking-tight
                           text-[2.1rem] sm:text-[2.6rem] lg:text-[3rem] font-light">
                {title}
            </h2>
            {subtitle && (
                <p className="mt-4 text-[14px] sm:text-base text-ink-soft max-w-xl leading-relaxed">
                    {subtitle}
                </p>
            )}
        </header>
    );
}
